import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Icon, Reveal, Overline, EASE } from "./primitives";
import { useScrollTo } from "./scroll";

const INDUSTRIES = {
  healthcare: {
    label: "Healthcare Solutions",
    icon: "HeartPulse",
    color: "#0D9488",
    soft: "#06B6D4",
    target: "healthcare",
    summary:
      "Connect patient care, diagnostics, pharmacy, billing and workforce management across hospitals, laboratories and pharmacies.",
    highlights: ["Hospital, OPD & IPD", "Laboratory & Diagnostics", "Pharmacy POS & Inventory", "Billing & Analytics"],
  },
  education: {
    label: "Education Solutions",
    icon: "GraduationCap",
    color: "#4F46E5",
    soft: "#7C3AED",
    target: "education",
    summary:
      "Manage admissions, academics, fees, examinations, communication and staff across schools, colleges and institutions.",
    highlights: ["Admissions & Student Info", "Fees & Accounts", "Examinations & Grades", "Parent & Teacher Portals"],
  },
};

export default function IndustrySelector() {
  const [active, setActive] = useState("healthcare");
  const scrollToId = useScrollTo();
  const data = INDUSTRIES[active];

  return (
    <section className="relative bg-white py-20 sm:py-28" data-testid="industry-selector">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div className="text-center">
            <Overline>Two industries. One platform.</Overline>
            <h2 className="mx-auto mt-4 max-w-2xl font-heading text-3xl font-extrabold tracking-tight text-ajmus-ink sm:text-4xl">
              Specialised products for your industry, unified underneath.
            </h2>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mx-auto mt-9 flex w-full max-w-md items-center rounded-full border border-black/5 bg-[#FAFAFA] p-1.5">
            {Object.entries(INDUSTRIES).map(([key, v]) => (
              <button
                key={key}
                onClick={() => setActive(key)}
                data-testid={`industry-toggle-${key}`}
                className="relative flex-1 rounded-full px-4 py-3 text-sm font-semibold transition-colors duration-200"
                style={{ color: active === key ? "#fff" : "#4B5563" }}
              >
                {active === key && (
                  <motion.span
                    layoutId="industry-pill"
                    className="absolute inset-0 -z-10 rounded-full"
                    style={{ backgroundColor: v.color }}
                    transition={{ duration: 0.4, ease: EASE }}
                  />
                )}
                {v.label}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mx-auto mt-10 max-w-4xl">
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.4, ease: EASE }}
              className="overflow-hidden rounded-3xl border border-black/5 bg-white p-8 shadow-[0_20px_60px_rgb(11,19,43,0.06)] sm:p-10"
              style={{ backgroundImage: `radial-gradient(120% 120% at 100% 0%, ${data.color}0d 0%, transparent 55%)` }}
            >
              <div className="flex flex-col gap-8 sm:flex-row sm:items-start">
                <span
                  className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl"
                  style={{ backgroundColor: `${data.color}14`, color: data.color }}
                >
                  <Icon name={data.icon} size={30} />
                </span>
                <div className="flex-1">
                  <h3 className="font-heading text-2xl font-extrabold tracking-tight text-ajmus-ink">
                    {data.label}
                  </h3>
                  <p className="mt-3 text-base leading-relaxed text-ajmus-steel">{data.summary}</p>
                  <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {data.highlights.map((h) => (
                      <div key={h} className="flex items-center gap-2.5 rounded-xl border border-black/5 bg-[#FAFAFA] px-4 py-3">
                        <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: data.color }} />
                        <span className="text-sm font-medium text-ajmus-ink">{h}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => scrollToId(data.target)}
                    data-testid={`industry-explore-${active}`}
                    className="group mt-7 inline-flex items-center gap-2 text-sm font-semibold"
                    style={{ color: data.color }}
                  >
                    Explore {data.label}
                    <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
                  </button>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
